import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  acceptOrgInvite,
  previewOrgInvite,
  registerFromInvite,
} from '../api';
import { errorMessage } from '../lib/api';
import { useOrg } from '../org/useOrg';
import { useAuth } from '../auth/useAuth';
import { Callout } from '../components/Callout';
import { Spinner } from '../components/Spinner';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { followSessionSlot } from '../lib/accountSlot';

export function InvitationAcceptPage() {
  useDocumentTitle('Accept invitation');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setActiveOrg } = useOrg();
  const { isAuthenticated, initializing, setSession, user } = useAuth();
  const queryClient = useQueryClient();
  const token = searchParams.get('token') ?? '';

  // Preview is public — fetch it in every state so we can show the workspace
  // name in the confirmation, whether or not the visitor is signed in yet.
  const preview = useQuery({
    queryKey: ['invite-preview', token],
    queryFn: () => previewOrgInvite(token),
    enabled: !!token && !initializing,
    retry: false,
  });

  const inviteUrl = `/invitations/accept?token=${token}`;

  // Joining is always an explicit click — never automatic on load or on
  // sign-in / account creation.
  const accept = useMutation({
    mutationFn: () => acceptOrgInvite(token),
    onSuccess: async (data) => {
      // Refetch the workspace list so it includes the one we just joined, then
      // go straight in (an invited user had no workspace until now, so this
      // avoids a flash of the no-workspace screen).
      await queryClient.invalidateQueries({ queryKey: ['orgs'] });
      setActiveOrg(data.orgId);
      navigate(`/w/${data.orgSlug}`, { replace: true });
    },
  });

  // New user: create the account + sign in, but DON'T join — the page then
  // re-renders authenticated and shows the same confirm step.
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const register = useMutation({
    mutationFn: () => registerFromInvite(token, password),
    onSuccess: (data) => {
      setSession(data);
      followSessionSlot(data, inviteUrl);
    },
  });

  const onRegisterSubmit = (e: FormEvent) => {
    e.preventDefault();
    setValidationError(null);
    if (password.length < 8) {
      setValidationError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setValidationError('Passwords do not match.');
      return;
    }
    register.mutate();
  };

  const shell = (children: ReactNode, width = 'max-w-sm') => (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className={`w-full ${width} space-y-4`}>{children}</div>
    </div>
  );

  if (!token) {
    return shell(
      <div className="card p-6 text-center">
        <p className="text-sm text-danger">Invalid invitation link — no token provided.</p>
      </div>,
    );
  }

  if (initializing || preview.isLoading) {
    return shell(
      <div className="card p-6 text-center">
        <Spinner className="mx-auto h-6 w-6" />
      </div>,
    );
  }

  if (preview.isError) {
    return shell(
      <div className="card p-6 text-center space-y-3">
        <Callout tone="danger">{errorMessage(preview.error)}</Callout>
        <p className="text-sm text-ink-muted">
          The invitation link may have expired or already been used.
        </p>
      </div>,
    );
  }

  const inviteData = preview.data!;

  // ── Authenticated → confirm (no auto-accept) ──────────────────────────────
  if (isAuthenticated) {
    const mismatch =
      !!user?.email &&
      user.email.toLowerCase() !== inviteData.invitedEmail.toLowerCase();
    return shell(
      <>
        <div className="text-center">
          <h1 className="text-lg font-semibold text-ink">Join {inviteData.orgName}</h1>
          <p className="mt-1 text-sm text-ink-muted">
            You’ve been invited to join as{' '}
            <span className="font-medium">{inviteData.invitedEmail}</span>.
          </p>
        </div>
        <div className="card space-y-3 p-6">
          {mismatch ? (
            <>
              <Callout tone="warning">
                You’re signed in as <span className="font-medium">{user!.email}</span>,
                but this invitation is for {inviteData.invitedEmail}.
              </Callout>
              <button
                type="button"
                className="btn-primary w-full"
                onClick={() => navigate('/login', { state: { from: inviteUrl } })}
              >
                Sign in as {inviteData.invitedEmail}
              </button>
            </>
          ) : (
            <>
              {accept.isError && (
                <Callout tone="danger">{errorMessage(accept.error)}</Callout>
              )}
              <button
                type="button"
                className="btn-primary w-full"
                disabled={accept.isPending}
                onClick={() => accept.mutate()}
              >
                {accept.isPending ? 'Joining…' : 'Join workspace'}
              </button>
              <button
                type="button"
                className="btn-secondary w-full"
                onClick={() => navigate('/', { replace: true })}
              >
                Not now
              </button>
            </>
          )}
        </div>
      </>,
    );
  }

  // ── Unauthenticated, existing account → sign in, then confirm ─────────────
  if (inviteData.hasAccount) {
    return shell(
      <>
        <div className="text-center">
          <h1 className="text-lg font-semibold text-ink">Join {inviteData.orgName}</h1>
          <p className="mt-1 text-sm text-ink-muted">
            You were invited as <span className="font-medium">{inviteData.invitedEmail}</span>.
            You already have an account — sign in to continue.
          </p>
        </div>
        <div className="card p-6">
          <button
            type="button"
            className="btn-primary w-full"
            onClick={() => navigate('/login', { state: { from: inviteUrl } })}
          >
            Sign in to continue
          </button>
        </div>
      </>,
    );
  }

  // ── Unauthenticated, new email → create account (then confirm) ────────────
  return shell(
    <>
      <div className="text-center">
        <h1 className="text-lg font-semibold text-ink">Join {inviteData.orgName}</h1>
        <p className="mt-1 text-sm text-ink-muted">
          You were invited as <span className="font-medium">{inviteData.invitedEmail}</span>.
          Set a password to create your account.
        </p>
      </div>

      <form onSubmit={onRegisterSubmit} className="card space-y-4 p-6">
        <div>
          <label className="label" htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            required
            autoFocus
          />
        </div>
        <div>
          <label className="label" htmlFor="confirm-password">Confirm password</label>
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            className="input"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Repeat password"
            required
          />
        </div>

        {(validationError || register.isError) && (
          <Callout tone="danger">
            {validationError ?? errorMessage(register.error)}
          </Callout>
        )}

        <button type="submit" className="btn-primary w-full" disabled={register.isPending}>
          {register.isPending && <Spinner />}
          {register.isPending ? 'Creating account…' : 'Create account'}
        </button>
      </form>

      <p className="text-center text-sm text-ink-muted">
        Already have an account?{' '}
        <Link to="/login" state={{ from: inviteUrl }} className="text-ink hover:underline">
          Sign in
        </Link>
      </p>
    </>,
  );
}
